import {
  IChecklistCategory,
  IChecklistItem,
  IChecklistItemRow,
  IChecklistPopulated,
  IChecklistProgress,
} from "@mctypes/checklist.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRowMoveInfo } from "@mcapps/shared/schemas/checklistSchema";
import { apiSlice } from "../../../apiSlice";
import { addAppModalThunk, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { AppModalEnums, ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { AppThunk } from "../../../store";
import { sortArrayByNumber } from "../../utils";
import { doCreateIssueDepricated } from "../issuesApp/issueAPIDepricated";
import {
  doAddChecklistCategory,
  doAddChecklistItem,
  doConnectOrDisconnectIssueFromChecklistItem,
  doGetSingleChecklistItem,
  doGetSingleProjectChecklistParts,
  doRemoveChecklistCategory,
  doRemoveChecklistItem,
  doUpdateChecklist,
  doUpdateChecklistCategory,
  doUpdateChecklistItem,
  doUpdateChecklistRowPosition,
} from "./checklistAPI";
import { getItemCount } from "./checklistUtils";

export interface IChecklistReducer {
  checklist: IChecklistPopulated;
  checklistCategories: IChecklistCategory[];
  checklistItems: IChecklistItem[];
  progress: IChecklistProgress;
  activeTab: string;
  checklistTableVariant: ChecklistTableVariant;
  checklistIdToConnectToANewIssue: string;
  activeProjectChecklistsCreatedByLoggedInUser: IChecklistPopulated[];
  freeTextSearch: string;
  selectedPnsFilterItem: string;
  hiddenColumns: string[];
  tempHiddenCategories: string[];
  refactoredCollapsedCategoryIds: string[];
  refactoredExpandedCategoryIds: string[];
  mcTemplateTestPage: {
    selectedTemplateNrId: number | null;
  };
  showInDevelopment: boolean;
}

export const CHECKLIST_TABLE_VARIANT_STORAGE_KEY = "checklistTableVariant";
export type ChecklistTableVariant = "legacy" | "refactored";

const initialState: IChecklistReducer = {
  checklist: null,
  checklistItems: [],
  checklistCategories: [],
  activeTab: "CHECKLIST",
  checklistTableVariant:
    localStorage.getItem(CHECKLIST_TABLE_VARIANT_STORAGE_KEY) === "refactored" ? "refactored" : "legacy",
  progress: {
    accepted: 0,
    rejected: 0,
  },
  checklistIdToConnectToANewIssue: null,
  activeProjectChecklistsCreatedByLoggedInUser: [],
  freeTextSearch: "",
  selectedPnsFilterItem: null,
  hiddenColumns: [],
  tempHiddenCategories: [],
  refactoredCollapsedCategoryIds: [],
  refactoredExpandedCategoryIds: [],
  mcTemplateTestPage: {
    selectedTemplateNrId: null,
  },
  showInDevelopment: false,
};

export const getChecklistThunk =
  (checklistId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const { categories, checklist, items } = await doGetSingleProjectChecklistParts(project._id, checklistId);

    dispatch(updateChecklistProgressInRedux(checklist.progress));
    dispatch(setCurrentChecklistInRedux(checklist));
    dispatch(setChecklistItemsInRedux(sortArrayByNumber(items, "itemId")));
    dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(categories, "categoryId")));
  };

export const updateChecklistThunk =
  (
    attribute: string,
    oldValue: unknown,
    newValue: unknown,
    checklistIdOverride?: string,
    projectIdOverride?: string,
    successMessage?: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const projectId = projectIdOverride ?? project?._id;
    const checklistId = checklistIdOverride ?? checklist?._id;

    if (projectId == null) {
      throw new Error("No project selected");
    }

    if (checklistId == null) {
      throw new Error("No checklist selected");
    }

    const updatedChecklist = await doUpdateChecklist(projectId, checklistId, attribute, newValue, oldValue);

    dispatch(updateChecklistProgressInRedux(updatedChecklist.progress));
    dispatch(updateChecklistInRedux(updatedChecklist));
    if (successMessage != null) {
      dispatch(setShowSuccessMessageThunk(successMessage));
    }

    dispatch(apiSlice.util.invalidateTags(["Checklist"]));
  };

export const updateChecklistCategoryThunk =
  <T extends keyof IChecklistItemRow>(
    categoryId: string,
    param: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const categories = getState().checklistReducer.checklistCategories;

    const updatedChecklistCategory = await doUpdateChecklistCategory(
      project._id,
      checklist._id,
      categoryId,
      param,
      newValue,
      oldValue,
    );

    const updatedChecklistCategories: IChecklistCategory[] = sortArrayByNumber(
      categories.map((category) =>
        category._id === updatedChecklistCategory._id ? updatedChecklistCategory : category,
      ),
      "categoryId",
    );

    dispatch(updateChecklistCategoryInRedux(updatedChecklistCategories));
  };

export const updateChecklistItemThunk =
  <T extends keyof IChecklistItemRow>(
    itemId: string,
    path: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const items = getState().checklistReducer.checklistItems;

    const updatedChecklistItem = await doUpdateChecklistItem(
      project._id,
      checklist._id,
      itemId,
      path,
      newValue,
      oldValue,
    );
    const updatedChecklistItems: IChecklistItem[] = sortArrayByNumber(
      items.map((category) => (category._id === updatedChecklistItem._id ? updatedChecklistItem : category)),
      "itemId",
    );

    dispatch(updateChecklistProgressInRedux(updatedChecklistItem.progress));
    dispatch(updateChecklistItemsInRedux(updatedChecklistItems));
  };

export const updateChecklistRowPositionThunk =
  (moveInfo: IRowMoveInfo): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const updatedChecklistRows = await doUpdateChecklistRowPosition(project._id, moveInfo);

    if (moveInfo.isCat) {
      dispatch(
        setChecklistCategoriesInRedux(sortArrayByNumber(updatedChecklistRows as IChecklistCategory[], "categoryId")),
      );
    } else {
      dispatch(setChecklistItemsInRedux(sortArrayByNumber(updatedChecklistRows as IChecklistItem[], "itemId")));
    }
  };

export const deleteChecklistCategoryThunk =
  (categoryId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    await doRemoveChecklistCategory(project._id, checklist._id, categoryId);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    dispatch(deleteChecklistCategoryInRedux(categoryId));
  };

export const deleteChecklistItemThunk =
  (itemId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    await doRemoveChecklistItem(project._id, checklist._id, itemId);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    dispatch(deleteChecklistItemsInRedux(itemId));
  };

export const addChecklistCategoryThunk =
  (categoryCount: number): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    const addedChecklistCategory = await doAddChecklistCategory(project._id, checklist._id, categoryCount);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));

    dispatch(addChecklistCategoryInRedux(addedChecklistCategory));
  };

export const addChecklistItemThunk =
  (categoryId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const { checklist, checklistItems } = getState().checklistReducer;

    const itemCount = getItemCount(categoryId, checklistItems);
    const addedChecklistItem = await doAddChecklistItem(project._id, checklist._id, categoryId, itemCount);

    dispatch(addChecklistItemInRedux(addedChecklistItem));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

export const createIssueOnChecklistItemThunk =
  (issueBoardId: string, checklistId): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;

    const createdIssue = await doCreateIssueDepricated(
      project._id,
      issueBoardId,
      `Angi tittel`,
      "ISSUE",
      false,
      checklistId,
    );

    dispatch(
      addAppModalThunk({
        itemId: createdIssue._id,
        boardId: issueBoardId,
        projectId: project._id,
        app: AppModalEnums.ISSUE,
      }),
    );
    dispatch(fetchSingleChecklistItemAndReplaceInReduxThunk(checklistId));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

export const fetchSingleChecklistItemAndReplaceInReduxThunk =
  (checklistItemId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const { project } = getState().adminReducer;
    const checklistItem = await doGetSingleChecklistItem(project._id, checklistItemId);
    dispatch(updateSingleChecklistItemInRedux(checklistItem));
  };

export const connectOrDisconnectIssueFromChecklistItemThunk =
  (checklistItemId: string, issueId: string, connectOrDisconnect: "CONNECT" | "DISCONNECT"): AppThunk =>
  async (dispatch: any, getState) => {
    await doConnectOrDisconnectIssueFromChecklistItem(getState().adminReducer.project._id, {
      checklistItemId,
      issueId,
      connectOrDisconnect,
    });
    dispatch(fetchSingleChecklistItemAndReplaceInReduxThunk(checklistItemId));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

export const tempCollapseAllCategoriesThunk = (collapsed: boolean): AppThunk => {
  return (dispatch: any, getState) => {
    const { checklistCategories, tempHiddenCategories } = getState().checklistReducer;
    let updatedChecklistCategories: IChecklistCategory[];
    let updatedTempHiddenCategories: string[] = tempHiddenCategories;

    if (collapsed === true) {
      updatedTempHiddenCategories = checklistCategories.filter((cat) => cat.collapsed === true).map((cat) => cat._id);
      updatedChecklistCategories = checklistCategories.map((cat) => {
        const catTemp = { ...cat };
        catTemp.collapsed = true;
        return catTemp;
      });
    } else {
      updatedChecklistCategories = checklistCategories.map((cat) => {
        const catTemp = { ...cat };

        if (updatedTempHiddenCategories.includes(catTemp._id) === false) {
          catTemp.collapsed = false;
        }

        return catTemp;
      });
    }

    dispatch(
      toggleTempCollapseAllCategoriesInRedux({ cat: updatedChecklistCategories, hidden: updatedTempHiddenCategories }),
    );
  };
};

export const checklistSlice = createSlice({
  name: "checklist",
  initialState,
  reducers: {
    resetChecklistInRedux: (state) => {
      Object.assign(state, initialState);
    },
    addChecklistCategoryInRedux: (state, action: PayloadAction<IChecklistCategory>) => {
      state.checklistCategories = [...state.checklistCategories, action.payload];
    },
    addChecklistItemInRedux: (state, action: PayloadAction<IChecklistItem>) => {
      state.checklistItems = [...state.checklistItems, action.payload];
    },
    setActiveChecklistTabInRedux: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setChecklistTableVariantInRedux: (state, action: PayloadAction<ChecklistTableVariant>) => {
      state.checklistTableVariant = action.payload;
      localStorage.setItem(CHECKLIST_TABLE_VARIANT_STORAGE_KEY, action.payload);
    },
    setCurrentChecklistInRedux: (state, action: PayloadAction<IChecklistPopulated>) => {
      state.checklist = action.payload;
    },
    setChecklistCategoriesInRedux: (state, action: PayloadAction<IChecklistCategory[]>) => {
      state.checklistCategories = action.payload.map((cat) => {
        const prev = state.checklistCategories.find((prevCat) => prevCat._id === cat._id);
        const collapsed = cat.collapsed ?? false;
        return { ...cat, collapsed };
      });
    },
    setChecklistItemsInRedux: (state, action: PayloadAction<IChecklistItem[]>) => {
      state.checklistItems = action.payload;
    },
    updateChecklistInRedux: (state, action: PayloadAction<IChecklistPopulated>) => {
      state.checklist = action.payload;
    },
    updateChecklistCategoryInRedux: (state, action: PayloadAction<IChecklistCategory[]>) => {
      state.checklistCategories = action.payload;
    },
    updateChecklistItemsInRedux: (state, action: PayloadAction<IChecklistItem[]>) => {
      state.checklistItems = action.payload;
    },
    updateChecklistProgressInRedux: (state, action: PayloadAction<IChecklistProgress>) => {
      state.progress = action.payload ?? {
        accepted: 0,
        rejected: 0,
      };
    },
    deleteChecklistCategoryInRedux: (state, action: PayloadAction<string>) => {
      state.checklistCategories = state.checklistCategories.filter((category) => category._id !== action.payload);
    },
    deleteChecklistItemsInRedux: (state, action: PayloadAction<string>) => {
      state.checklistItems = state.checklistItems.filter((item) => item._id !== action.payload);
    },
    toggleAllCategoryCollapseInRedux: (state, action: PayloadAction<boolean>) => {
      state.checklistCategories = state.checklistCategories.map((cat) => {
        return { ...cat, collapsed: action.payload };
      });
    },
    toggleTempCollapseAllCategoriesInRedux: (
      state,
      action: PayloadAction<{ cat: IChecklistCategory[]; hidden: string[] }>,
    ) => {
      state.tempHiddenCategories = action.payload.hidden;
      state.checklistCategories = action.payload.cat;
    },
    toggleCategoryCollapseInRedux: (state, action: PayloadAction<string>) => {
      state.checklistCategories = state.checklistCategories.map((cat) => {
        if (cat._id === action.payload) {
          return { ...cat, collapsed: cat.collapsed !== true };
        }
        return cat;
      });
    },
    setRefactoredCollapsedCategoryIdsInRedux: (state, action: PayloadAction<string[]>) => {
      state.refactoredCollapsedCategoryIds = action.payload;
    },
    setRefactoredExpandedCategoryIdsInRedux: (state, action: PayloadAction<string[]>) => {
      state.refactoredExpandedCategoryIds = action.payload;
    },
    updateSingleChecklistItemInRedux: (state, action: PayloadAction<IChecklistItem>) => {
      state.checklistItems = state.checklistItems.map((item) =>
        item._id === action.payload?._id ? action.payload : item,
      );
    },
    setSelectedPnsFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsFilterItem = action.payload;
    },
    setChecklistFreeTextSearchInRedux: (state, action: PayloadAction<string>) => {
      state.freeTextSearch = action.payload;
    },
    toggleColVisibility: (state, action: PayloadAction<{ keys: string[]; bool: boolean }>) => {
      action.payload.keys.forEach((key) => {
        if (action.payload.bool === false) {
          if (!state.hiddenColumns.includes(key)) {
            state.hiddenColumns.push(key);
          }
        } else {
          state.hiddenColumns = state.hiddenColumns.filter((column) => column !== key);
        }
      });
    },
    setSelectedTemplateNrIdInRedux: (state, action: PayloadAction<number | null>) => {
      state.mcTemplateTestPage.selectedTemplateNrId = action.payload;
    },
    setShowChecklistTemplatesInDevelopmentInRedux: (state, action: PayloadAction<boolean>) => {
      state.showInDevelopment = action.payload;
    },
  },
});

export const {
  addChecklistCategoryInRedux,
  addChecklistItemInRedux,
  setCurrentChecklistInRedux,
  updateChecklistProgressInRedux,
  setActiveChecklistTabInRedux,
  setChecklistTableVariantInRedux,
  setChecklistItemsInRedux,
  setChecklistCategoriesInRedux,
  updateChecklistInRedux,
  updateChecklistCategoryInRedux,
  updateChecklistItemsInRedux,
  deleteChecklistCategoryInRedux,
  deleteChecklistItemsInRedux,
  toggleAllCategoryCollapseInRedux,
  toggleCategoryCollapseInRedux,
  updateSingleChecklistItemInRedux,
  setSelectedPnsFilterItemInRedux,
  setChecklistFreeTextSearchInRedux,
  toggleColVisibility,
  toggleTempCollapseAllCategoriesInRedux,
  setRefactoredCollapsedCategoryIdsInRedux,
  setRefactoredExpandedCategoryIdsInRedux,
  resetChecklistInRedux,
  setSelectedTemplateNrIdInRedux,
  setShowChecklistTemplatesInDevelopmentInRedux,
} = checklistSlice.actions;

export default checklistSlice.reducer;
